import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { ActionButton } from 'app/components/Button/ActionButton';
import { CustomCheckbox } from 'app/components/Checkbox/CheckboxDefault';
import InputSearch from 'app/components/Input/InputSearch';
import { FormModal } from 'app/components/Modal/FormModal';
import { CustomHeader, FormTableHeader } from 'app/components/Modal/components/CustomHeader';
import { FormSelect } from 'app/components/Select/FormSelect';
import Typography from 'app/components/Typography';

import { trimValue } from 'app/helpers';
import { filterBySearch } from 'app/helpers/utils';
import { useBoolean, useNumber, useString } from 'app/hooks';
import { cloneDeep, countBy, maxBy, omit, orderBy, xorBy } from 'lodash';

import { Rack } from '../../racks/core/type';
import ViewItemInBinDetail from '../components/ViewItemInBinDetail';
import { StockLevel } from '../core/enum';
import { getStockLevel } from '../core/helpers';
import { StockRunItem } from '../core/type';
import { useOrderStore } from '../store';

const SelectAll = styled.div`
  width: 5%;
  display: flex;
  align-items: center;
  p {
    font-weight: 700;
    white-space: nowrap;
  }
  padding: 0 12px 16px 12px;
`;

interface Props {
  onClose: () => void;
  isOpen: boolean;
  onSubmitSelection: (selectBins: StockRunItem[]) => void;
  originStockRunItems: StockRunItem[];
  wardId: number;
  racks: Rack[];
}
const FormAddItemStockRun: React.FC<Props> = ({
  isOpen,
  originStockRunItems,
  onClose,
  onSubmitSelection,
  racks,
}) => {
  const rackId = useNumber(0);
  const isLoading = useBoolean(true);
  const searchItem = useString('');

  const sortOrderBy = useOrderStore((s) => s.state.orderBy);
  const sortOrderDirection = useOrderStore((s) => s.state.orderDirection);

  const [selectStockRunItems, setSelectStockRunItems] = useState<StockRunItem[]>([]);
  const modalRef = useRef<any>(null);

  const binItems = useMemo(() => {
    const itemsOfBin = rackId.value
      ? racks.find((el) => el.id === rackId.value)?.bins || []
      : racks.flatMap((rack) => rack.bins) || [];

    const rack = racks.find((r) => r.id === rackId.value);

    return itemsOfBin
      .filter((el) => el.item)
      .map(({ item, ...restBin }) => {
        return {
          bin: restBin,
          inventory: { ...item },
          rack,
          quantity: '0',
          order: 0,
          uid: `bin-${restBin.id}`,
          ...omit(item, 'id'),
        } as StockRunItem;
      });
  }, [racks, rackId.value]);

  const displayItems: StockRunItem[] = useMemo(() => {
    const newDataSort = orderBy(
      binItems.map((item) => ({
        ...item,
        stockLevel: Object.values(StockLevel).indexOf(getStockLevel(item)),
      })),
      sortOrderBy,
      sortOrderDirection === 'ASC' ? 'asc' : 'desc',
    );
    return filterBySearch(newDataSort, trimValue(searchItem.value), ['name', 'skuId', 'bin.name']);
  }, [searchItem.value, sortOrderBy, sortOrderDirection, binItems]);

  const selectedBinIds = useMemo(
    () => new Set(selectStockRunItems.filter((item) => item.bin?.id).map((item) => item.bin!.id)),
    [selectStockRunItems],
  );

  const currentRackBinIds = binItems.map((item) => item.bin?.id).filter(Boolean);
  const selectedBinIdsInCurrentRack = currentRackBinIds.filter((binId) =>
    selectedBinIds.has(binId),
  );

  const isSelectAll =
    currentRackBinIds.length > 0 && currentRackBinIds.every((binId) => selectedBinIds.has(binId));

  useEffect(() => {
    modalRef.current.scrollToTop();
  }, [rackId.value]);

  useEffect(() => {
    const timer = setTimeout(() => {
      isLoading.setValue(false);
    }, 200);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectStockRunItems(originStockRunItems);
  }, [isOpen]);

  useEffect(() => {
    useOrderStore.getState().clearState();
  }, []);

  const onChangeRack = (newValue: number) => {
    rackId.setValue(newValue);
  };

  const onSelectStockRunItem = (binItem: StockRunItem) => () => {
    const binId = binItem.bin?.id;
    if (!binId) return;

    setSelectStockRunItems((prev) => {
      const hasItemsFromThisBin = prev.some((item) => item.bin?.id === binId);

      if (hasItemsFromThisBin) {
        return prev.filter((item) => item.bin?.id !== binId);
      }

      const newItem = {
        ...binItem,
        uid: `${binId}-${Date.now()}-${Math.random()}`,
        quantity: '0',
      };
      return [...prev, newItem];
    });
  };

  const onChangeSelectAllItem = (newSelectAll: boolean) => {
    if (!newSelectAll) {
      const rackBinIds = new Set(binItems.map((item) => item.bin?.id));
      setSelectStockRunItems((prev) => prev.filter((item) => !rackBinIds.has(item.bin?.id)));
      return;
    }

    setSelectStockRunItems((prev) => {
      const existingBinIds = new Set(prev.map((item) => item.bin?.id));
      const newItems = binItems
        .filter((item) => !existingBinIds.has(item.bin?.id))
        .map((item) => ({
          ...item,
          uid: `${item.bin?.id}-${Date.now()}-${Math.random()}`,
          quantity: '0',
        }));
      return [...prev, ...newItems];
    });
  };

  const handleInputChangeSearchItem = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchItem.setValue(event.target.value);
  };

  const onSubmitAdd = () => {
    let maxOrder = maxBy(originStockRunItems, 'order')?.order ?? -1;
    const newItemCount = countBy(selectStockRunItems, (el) => el.order === undefined).true;
    const newItemsWithOrder = selectStockRunItems.map((el) => {
      if (typeof el.order === 'number') {
        return el;
      }
      maxOrder--;
      return {
        ...el,
        order: maxOrder + 1 + newItemCount,
      };
    });
    onSubmitSelection(newItemsWithOrder);
  };

  return (
    <FormModal
      ref={modalRef}
      extraBodyStyle={{
        position: 'static',
        zIndex: 0,
      }}
      title="Add items"
      onClickCloseModal={onClose}
      childrenAction={
        <ActionButton
          onCancel={onClose}
          disableButton={selectStockRunItems.length === originStockRunItems.length}
          isSubmitting={false}
          onSubmit={onSubmitAdd}
          labelButtonSubmit="Select"
        />
      }
      styleModalDialog={{ minWidth: 1000 }}
      showLoading={isLoading.value}
      Heading={
        <div className="d-flex justify-content-between" style={{ padding: '28px 28px 0 28px' }}>
          <div style={{ width: 200 }}>
            <FormSelect
              label="Rack"
              handleChange={(select) => onChangeRack(select.value)}
              optionsSelect={[{ title: 'All racks', value: 0 }].concat(
                racks.map((el) => {
                  return {
                    title: el.name,
                    value: Number(el.id),
                  };
                }),
              )}
              activeSelect={rackId.value}
              fieldActive="value"
              fieldSelect="title"
              styleSelectCard={{
                width: 200,
              }}
              styleRoot={{ marginBottom: 0 }}
            />
          </div>
          <div
            style={{
              marginTop: 30,
            }}
          >
            <InputSearch
              value={searchItem.value}
              onChange={handleInputChangeSearchItem}
              placeholder="Search with item name, SKU, or Bin"
            />
          </div>
        </div>
      }
    >
      {!isLoading.value && (
        <>
          <FormTableHeader>
            <CustomHeader title="Item" styleHeader={{ width: '30%' }} id="inventory.name" />
            <CustomHeader title="SKU" styleHeader={{ width: '15%' }} id="inventory.skuId" />
            <CustomHeader
              title="Rack"
              styleHeader={{
                width: '15%',
              }}
              id="rack.name"
            />
            <CustomHeader
              title="Bin"
              styleHeader={{
                width: '15%',
              }}
              id="bin.name"
            />
            <CustomHeader
              title="Location quantity"
              styleHeader={{ width: '10%' }}
              id="inventory.qty"
              titleExtraClass="whitespace-pre-wrap"
            />
            <CustomHeader
              title="Location health"
              styleHeader={{ width: '10%', whiteSpace: 'normal' }}
              id="stockLevel"
            />
            <SelectAll>
              {Boolean(binItems.length) && (
                <CustomCheckbox
                  checked={isSelectAll}
                  onChange={() => onChangeSelectAllItem(!isSelectAll)}
                  indeterminate={selectedBinIdsInCurrentRack.length > 0 && !isSelectAll}
                />
              )}
            </SelectAll>
          </FormTableHeader>
          {displayItems.length ? (
            displayItems.map((binItem, index) => (
              <ViewItemInBinDetail
                stockRunItem={binItem}
                key={binItem.uid}
                selectStockRunItems={Array.from(selectedBinIds)}
                onSelect={onSelectStockRunItem}
                highlight={index % 2 === 0}
              />
            ))
          ) : (
            <div className="d-flex justify-content-center" style={{ padding: '16px 10px' }}>
              <Typography variant="body-3">No matching records found</Typography>
            </div>
          )}
        </>
      )}
    </FormModal>
  );
};
export default FormAddItemStockRun;
